import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';

const TOOL_PRIMARY_FIELD = {
  sermon: 'passage',
  announcement: 'eventName',
  discussion: 'sermonTitle',
  devotional: 'theme',
  pastoral: 'situationType',
  evangelism: 'context',
  prayer: 'requestCategory',
  series: 'theme',
  biblestudy: 'passage',
  eventpromo: 'eventName',
};

const mdComponents = {
  h1: ({ children }) => <h1 className="text-base font-bold mt-3 mb-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mt-3 mb-1">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1">{children}</h3>,
  p: ({ children }) => <p className="mb-2 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-gray-800">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-gray-400 pl-3 italic my-2 text-gray-600">{children}</blockquote>
  ),
  hr: () => <hr className="my-3 border-gray-300" />,
};

function formatDate(isoString) {
  const d = new Date(isoString);
  return (
    d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) +
    ' at ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
}

function getPrimaryInput(toolName, inputJson) {
  try {
    const parsed = JSON.parse(inputJson);
    const field = TOOL_PRIMARY_FIELD[toolName];
    const val =
      field && parsed[field]
        ? parsed[field]
        : Object.values(parsed).find((v) => v && typeof v === 'string') || '';
    return val.length > 80 ? val.slice(0, 77) + '...' : val;
  } catch {
    return (inputJson || '').slice(0, 80);
  }
}

const PAGE_SIZE = 5;

export default function ToolHistory({
  token,
  toolName,
  displayName,
  onAuthExpired,
  newGenerationCount,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [error, setError] = useState('');

  const searchTimeout = useRef(null);
  const prevGenCount = useRef(0);

  const doFetch = useCallback(
    async (search, offset, append) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ limit: PAGE_SIZE, offset });
        if (search) params.set('search', search);
        const res = await fetch(`/api/history/${toolName}?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.status === 401) {
          if (onAuthExpired) onAuthExpired();
          return;
        }
        if (!res.ok) {
          setError(data.error || 'Failed to load history.');
          return;
        }
        setTotal(data.total);
        setItems((prev) => (append ? [...prev, ...data.history] : data.history));
      } catch {
        setError('Error loading history.');
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    },
    [token, toolName, onAuthExpired]
  );

  // Initial load
  useEffect(() => {
    doFetch('', 0, false);
  }, [doFetch]);

  // Prepend the newest item after a new generation completes
  useEffect(() => {
    if (!newGenerationCount || newGenerationCount === prevGenCount.current) return;
    prevGenCount.current = newGenerationCount;
    (async () => {
      try {
        const res = await fetch(`/api/history/${toolName}?limit=1&offset=0`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data.history.length > 0) {
          const latest = data.history[0];
          setItems((prev) =>
            prev.find((i) => i.id === latest.id) ? prev : [latest, ...prev]
          );
          setTotal((prev) => prev + 1);
        }
      } catch {}
    })();
  }, [newGenerationCount, toolName, token]);

  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setActiveSearch(val);
      doFetch(val, 0, false);
    }, 400);
  };

  const clearSearch = () => {
    setSearchText('');
    setActiveSearch('');
    clearTimeout(searchTimeout.current);
    doFetch('', 0, false);
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`/api/history/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setItems((prev) => prev.filter((i) => i.id !== id));
        setTotal((prev) => Math.max(0, prev - 1));
        setConfirmDeleteId(null);
      }
    } catch {}
  };

  const showing = items.length;
  const hasMore = showing < total;

  return (
    <div className="mt-8">
      {/* Divider */}
      <div className="border-t border-gray-200 mb-6" />

      {/* Section header */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-gray-400">🕐</span>
        <h3 className="text-base font-semibold text-gray-700">
          Your {displayName} History
        </h3>
      </div>

      {/* Search bar */}
      <div className="relative mb-3">
        <input
          type="text"
          placeholder={`Search your ${displayName.toLowerCase()} history...`}
          value={searchText}
          onChange={handleSearchChange}
          className="w-full border border-gray-300 rounded-lg px-4 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900"
        />
        {searchText && (
          <button
            onClick={clearSearch}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Search result count */}
      {activeSearch && !loading && (
        <p className="text-xs text-gray-500 mb-3">
          {total === 0
            ? `No results found for "${activeSearch}"`
            : `${total} result${total !== 1 ? 's' : ''} found`}
        </p>
      )}

      {loading && (
        <p className="text-sm text-gray-400 py-4 text-center">Loading...</p>
      )}

      {error && <p className="text-xs text-red-500 py-2">{error}</p>}

      {!loading && !error && items.length === 0 && !activeSearch && (
        <p className="text-sm text-gray-400 py-4 text-center">
          No previous generations yet. Your work will appear here.
        </p>
      )}

      {/* Item list */}
      {!loading &&
        items.map((item) => (
          <div
            key={item.id}
            className="border border-gray-200 rounded-lg mb-3 overflow-hidden bg-white"
          >
            <div className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 truncate">
                  {getPrimaryInput(toolName, item.input)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {formatDate(item.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() =>
                    setExpandedId(expandedId === item.id ? null : item.id)
                  }
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium"
                >
                  {expandedId === item.id ? 'Hide' : 'View'}
                </button>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(item.output);
                    setCopiedId(item.id);
                    setTimeout(() => setCopiedId(null), 2000);
                  }}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium"
                >
                  {copiedId === item.id ? 'Copied!' : 'Copy'}
                </button>
                {confirmDeleteId === item.id ? (
                  <span className="flex items-center gap-1">
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-xs px-2 py-1.5 rounded-lg border border-red-400 bg-red-50 text-red-700 font-medium"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className="text-xs px-2 py-1.5 text-gray-400 hover:text-gray-600"
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(item.id)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 font-medium"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            {expandedId === item.id && (
              <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                <div
                  className="rounded-lg p-4 text-sm text-gray-800 max-h-96 overflow-y-auto"
                  style={{ backgroundColor: '#F5F5F5' }}
                >
                  <ReactMarkdown components={mdComponents}>
                    {item.output}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))}

      {/* Load more + count */}
      {!loading && items.length > 0 && (
        <div className="flex items-center gap-4 mt-1 mb-8">
          {hasMore && (
            <button
              onClick={() => doFetch(activeSearch, items.length, true)}
              disabled={loadingMore}
              className="text-sm px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium disabled:opacity-50 transition-colors"
            >
              {loadingMore ? 'Loading...' : 'Load 5 more'}
            </button>
          )}
          <span className="text-xs text-gray-400">
            Showing {showing} of {total} total
          </span>
        </div>
      )}
    </div>
  );
}
